import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { SelectModule } from 'primeng/select';

import { VariantAttributeService } from '../../core/services/variant-attribute.service';
import { StoreService } from '../../core/services/store.service';
import { VariantAttributeDto } from '../../core/models/variant.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';

interface ValueRow {
  value: string;
  extra: string;
  sortOrder: number;
}

@Component({
  selector: 'app-variant-attributes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    TagModule,
    ConfirmDialogModule,
    TooltipModule,
    SelectModule,
    TranslatePipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './variant-attributes.component.html',
})
export class VariantAttributesComponent implements OnInit {
  private attrSvc = inject(VariantAttributeService);
  private storeSvc = inject(StoreService);
  private confirmSvc = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  attributes = signal<VariantAttributeDto[]>([]);
  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda tanlagich yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  loading = signal(false);
  search = '';

  // ── Create attribute dialog ─────────────────────────────────────────────────
  createVisible = false;
  saving = false;
  form: { name: string; storeId: string | null; values: ValueRow[] } = {
    name: '',
    storeId: null,
    values: [],
  };

  /** Tanlangan do'konning storeType'i — atribut shu turga bog'lanadi. */
  selectedStoreTypeId(): string | null {
    if (!this.form.storeId) return null;
    return this.stores().find((s) => s.id === this.form.storeId)?.storeTypeId ?? null;
  }

  // ── Add value dialog (existing attribute) ───────────────────────────────────
  addValueVisible = false;
  addValueSaving = false;
  addValueTarget: VariantAttributeDto | null = null;
  valueForm: ValueRow = { value: '', extra: '', sortOrder: 0 };

  ngOnInit() {
    this.load();
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.stores.set(res.data ?? []) });
  }

  load() {
    this.loading.set(true);
    this.attrSvc
      .getAll(this.search || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.attributes.set(res.data ?? []),
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });
  }

  onSearch() {
    this.load();
  }

  // ── Create attribute ─────────────────────────────────────────────────────────

  openCreate() {
    this.form = {
      name: '',
      storeId: this.storePick.single()?.id ?? null,
      values: [{ value: '', extra: '', sortOrder: 0 }],
    };
    this.createVisible = true;
  }

  addValueRow() {
    this.form.values.push({ value: '', extra: '', sortOrder: this.form.values.length });
  }

  removeValueRow(index: number) {
    this.form.values.splice(index, 1);
  }

  closeCreate() {
    this.createVisible = false;
  }

  save() {
    const name = this.form.name.trim();
    if (!name) {
      this.attrSvc.showWarn(this.translate.instant('VARIANT_ATTRS.TOAST.NAME_REQUIRED'));
      return;
    }
    // Do'kon (storeType) majburiy — atribut shu turga bog'lanadi. Tanlagich
    // yashirilgan bo'lsa qiymat `openCreate()` da qo'yiladi; do'kon ro'yxati
    // kechikib kelgan holat uchun zaxira.
    this.form.storeId ??= this.storePick.single()?.id ?? null;
    if (!this.form.storeId) {
      this.attrSvc.showWarn(this.translate.instant('VARIANT_ATTRS.TOAST.STORE_REQUIRED'));
      return;
    }
    const values = this.form.values
      .filter((v) => v.value.trim())
      .map((v) => ({
        value: v.value.trim(),
        extra: v.extra.trim() || null,
        sortOrder: v.sortOrder,
      }));

    this.saving = true;
    this.attrSvc
      .create({ name, values, storeTypeId: this.selectedStoreTypeId() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.attrSvc.showCreateSuccess();
          this.closeCreate();
          this.load();
        },
        error: () => (this.saving = false),
        complete: () => (this.saving = false),
      });
  }

  // ── Add value to existing attribute ──────────────────────────────────────────

  openAddValue(attr: VariantAttributeDto) {
    this.addValueTarget = attr;
    this.valueForm = { value: '', extra: '', sortOrder: attr.values.length };
    this.addValueVisible = true;
  }

  closeAddValue() {
    this.addValueVisible = false;
    this.addValueTarget = null;
  }

  saveValue() {
    const target = this.addValueTarget;
    if (!target) return;
    const value = this.valueForm.value.trim();
    if (!value) {
      this.attrSvc.showWarn(this.translate.instant('VARIANT_ATTRS.TOAST.VALUE_REQUIRED'));
      return;
    }
    this.addValueSaving = true;
    this.attrSvc
      .addValue(target.id, {
        value,
        extra: this.valueForm.extra.trim() || null,
        sortOrder: this.valueForm.sortOrder,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.attrSvc.showCreateSuccess();
          this.closeAddValue();
          this.load();
        },
        error: () => (this.addValueSaving = false),
        complete: () => (this.addValueSaving = false),
      });
  }

  // ── Delete attribute ──────────────────────────────────────────────────────────

  confirmDelete(attr: VariantAttributeDto) {
    this.confirmSvc.confirm({
      message: this.translate.instant('VARIANT_ATTRS.DELETE_CONFIRM', { name: attr.name }),
      header: this.translate.instant('VARIANT_ATTRS.DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => {
        this.attrSvc
          .delete(attr.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.attrSvc.showDeleteSuccess();
              this.load();
            },
          });
      },
    });
  }

  valuesLabel(attr: VariantAttributeDto): string {
    return attr.values.map((v) => v.value).join(', ');
  }
}
